import { Fragment, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { PMField } from "../../../utils/PMField";
import { mapErrors } from "../../../Utils";
import { ResourceError } from "../../../utils/ResourceError";
import {
  ArrowUpOnSquareIcon,
  CheckBadgeIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ItemSelector from "../../../utils/Selector/ItemSelector";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import PMListbox from "../../../utils/PMListbox";
import {
  fetchCustomerSystem,
  fetchCustomerSystems,
  fetchSystemTypes,
} from "../../../../api/customer/system";
import {
  createSystemAsync,
  updateSystemAsync,
} from "../../../../slices/customer/system";
import ProductSlideover from "../../products/product-slideover";
import PMCombobox from "../../../utils/PMCombobox";
import { PMDialog } from "../../../utils/PMDialog";
import { PMButton } from "../../../utils/PMButton";
import ReportHistorySlideover from "../../service/reports/report-history-slideover";
import ItemSelectorV2 from "../../../utils/Selector/ItemSelectorV2";
import SystemForm from "./SystemForm";
import { PMCustomSelector } from "../../../utils/PMCustomSelector";
export default function CustomerSystemDialog(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [itemState, setItemState] = useState({
    isLoading: false,
    error: false,
    errors: [],
    system: {},
    systems: [],
    systemTypes: [],
  });

  // Deconstructing props
  const {
    open,
    selectedSystem,
    customer,
    onClose,
    selected,
    onAttachSystem,
    module,
    moduleParam,
    onRefresh,
  } = props;

  // Deconstruct itemState (Store)
  const { system, systems, systemTypes, isLoading, error, errors } = itemState;

  const [readOnly, setReadOnly] = useState(props.readOnly);

  const getCustomerSystems = async (customerId) => {
    return fetchCustomerSystems(customerId)
      .then((res) => {
        if (res?.data) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            systems: res?.data?.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err?.data?.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          systems: {},
          error: true,
          errors: errors,
        }));
      });
  };

  const getSystemTypes = async () => {
    return fetchSystemTypes()
      .then((res) => {
        if (res?.data) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            systemTypes: res?.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err?.data?.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          systems: {},
          error: true,
          errors: errors,
        }));
      });
  };

  const getCustomerSystem = async () => {
    setItemState((prevState) => ({ ...prevState, isLoading: true }));

    return fetchCustomerSystem(customer.id, selectedSystem)
      .then((res) => {
        if (res?.data?.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            system: res.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err?.data?.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          system: {},
          error: true,
          errors: errors,
        }));
      });
  };

  useEffect(() => {
    if (open) {
      if (selectedSystem) {
        getCustomerSystem();
      }
      getCustomerSystems(customer.id);
      getSystemTypes();
    }
  }, [open]);

  const createOrUpdateDispatch = (data) => {
    let res = null;
    if (system?.id) {
      res = dispatch(updateSystemAsync(data));
    } else {
      res = dispatch(createSystemAsync(data));
    }
    return res;
  };

  const systemOptions = () => {
    const label = (system) => {
      if (system?.product) {
        return system?.product?.title + " " + system.serial_number;
      } else {
        return system.serial_number;
      }
    };

    const array = [{ name: "system_id", label: "Ikke valgt", value: 0 }];
    systems?.map((system) => {
      array.push({
        name: "system_id",
        label: label(system),
        value: system.id,
      });
    });
    return array;
  };

  const systemTypeOptions = () => {
    const types = [];
    systemTypes?.map((type) => {
      types.push({
        name: "system_type_id",
        label: type.title,
        title: type.title,
        value: type.id,
      });
    });
    return types;
  };
  const [productSlideroverOpen, setProductSlideoverOpen] = useState(false);
  const [historySlideoverOpen, setHistorySlideoverOpen] = useState(false);

  const systemForm = (formik) => (
    <div className="mt-10">
      {system?.product ? (
        <>
          <div className="col-span-6 sm:col-span-6">
            <ul
              role="list"
              className="mt-6 divide-y divide-gray-200 border-t border-gray-200 text-sm font-medium text-gray-500"
            >
              <li key={0} className="flex space-x-6 py-6">
                <img className="h-24 w-24 flex-none rounded-md bg-gray-100 object-cover object-center" />
                <div className="flex-auto space-y-1">
                  <h3 className="text-gray-900">{system?.product?.title}</h3>
                  {system?.product?.item_number}
                  <p>{system?.product?.short_description}</p>
                </div>
                <Link className="cursor-pointer" to={"/dashboard/products/" + system?.product?.id} target="_blank">
                  <p className="flex-none font-medium text-gray-900">
                    <ArrowUpOnSquareIcon className="w-5 h-5 m-2" />
                  </p>
                </Link>
                <div className="cursor-pointer" onClick={() => setHistorySlideoverOpen(true)}>
                  <div className="h-15 inline-flex items-center rounded-md border border-transparent bg-blue-400 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                    {t("Historikk")}
                  </div>
                </div>
                {system?.product_id && (
                  <>
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        if (system?.product_id) {
                          setProductSlideoverOpen(true);
                        } else {
                          toast.error("No product selected");
                        }
                      }}
                    >
                      <div className="h-15 inline-flex items-center rounded-md border border-transparent bg-yellow-500 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                        {t("Reservedeler")}
                      </div>
                    </div>
                  </>
                )}
              </li>
            </ul>
          </div>
        </>
      ) : null}

      <SystemForm formik={formik} systemTypes={systemTypes} readOnly={readOnly} />
    </div>
  );

  const displayForm = (formik) => {
    if (!selectedSystem) {
      if (systems?.length > 0) {
        if (readOnly) {
          return attachSystem();
        } else {
          // Show existing systems first, then provide option to create new
          return (
            <div>
              <div className="mb-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {t("Existing Systems")}
                </h3>
                <div className="space-y-3">
                  {systems.map((sys) => (
                    <div
                      key={sys.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {sys.product?.title || sys.serial_number}
                        </div>
                        <div className="text-sm text-gray-500">
                          {sys.serial_number} - {sys.placement}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onAttachSystem({ value: sys.id })}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        {t("Velg")}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {t("Create New System")}
                </h3>
                {systemForm(formik)}
              </div>
            </div>
          );
        }
      } else {
        setReadOnly(false);
        return systemForm(formik);
      }
    } else {
      return systemForm(formik);
    }
  };
  const handleDetachSystem = (formik) => {
    if (onAttachSystem) {
      onAttachSystem({ value: null });
      // Refresh the report to show the detached system
      if (onRefresh && module === "report" && moduleParam) {
        onRefresh({ id: moduleParam });
      }
    }
  };

  // If a system is allready selected (selectedSystem), then displaySystem will be displayed.
  // You will be able to read and edit system
  const displaySystem = () => (
    <Formik
      enableReinitialize
      initialValues={{
        id: system?.id,
        customer_id: customer.id,
        product: system?.product,
        product_id: system?.product_id,
        system_m_name: system?.system_m_name,
        serial_number: system?.serial_number,
        installation_year: system?.installation_year,
        production_year: system?.production_year,
        system_type: system?.system_type,
        system_type_id: system?.type?.id,
        submit: null,
      }}
      validationSchema={Yup.object({
        serial_number: Yup.string().nullable().required(t("Serial Number is required")),
        // product_id: Yup.number()
        //   .typeError(t("Product is required"))
        //   .required(t("Product is required")),
      })}
      onSubmit={(values, helpers) => {
        try {
          toast.promise(createOrUpdateDispatch(values), {
            loading: t("Saving...."),
            success: (data) => {

              if (data?.status === 500) throw new Error("Server error");
              if (data?.payload?.errors)
                throw new Error(mapErrors(data?.payload?.errors));
              onClose(true);
              // After save or update, send the systemID to parent component

              if (data?.payload) {

                onAttachSystem({ value: data?.payload?.id });
                // Refresh the report to show the updated system
                if (onRefresh && module === "report" && moduleParam) {
                  onRefresh({ id: moduleParam });
                }
              }
              return t("Data was saved!");
            },
            error: (error) => {
              return t("Something went wrong. Could not save data!");
            },
          });
          // helpers.setStatus({ success: true });
        } catch (err) {
          toast.error("Something went wrong...");
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err?.message });
          // helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => {
        return (
          <Form>
            {displayForm(formik)}






            <div className="flex mt-10 sm:mt-6 gap-3 justify-between items-center">
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
              onClick={onClose}
            >
              {t("Close")}{" "}
            </button>

           <div className="flex justify-end w-full">
           {readOnly ? null : (
              <LoadingButton
                className="w-full"
                loading={formik.isSubmitting}
                disabled={isLoading}
                loadingPosition="start"
                startIcon={<CheckBadgeIcon />}
                type="button"
                variant="contained"
                color={"success"}
                onClick={formik.submitForm}
              >
                {system?.id ? t("Update") : t("Create")}
              </LoadingButton>
            )}
          </div>
           </div>
          </Form>
        );
      }}
    </Formik>
  );

  const attachSystem = (formik) => (
    <div className="grid grid-cols-6 gap-6 mt-10">
      <div className="col-span-6 sm:col-span-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t("Attach System")}
          </h3>
          <button
            type="button"
           
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            {t("Nytt aggregat")}
          </button>
        </div>
      </div>
      <div className="col-span-12 sm:col-span-12">
        <div className="flex items-center">
          <div className="w-full p-2">
            <PMListbox
              label={t("System")}
              options={systemOptions()}
              selected={null}
              onChange={onAttachSystem}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <PMDialog
        open={open}
        // onClose={onClose}
        title={
          readOnly
            ? t("Customer System")
            : system?.id
            ? t("Edit Customer System")
            : t("Create Customer System")
        }
        headerActions={[
          {
            label: t("Nytt aggregat"),
            action: () => setReadOnly(false),
            icon: PlusIcon,
            hidden: true

          },
          {
            label: t("Rediger"),
            action: () => setReadOnly(!readOnly),
            icon: PencilIcon,
          },

          {
            label: t("Detach System"),
            hidden: !system?.id,
            action: () => handleDetachSystem(),
            icon: XMarkIcon,
          },
          {
            label: t("Steng"),
            action: onClose,
            icon: XMarkIcon,
          },
        ]}
      >
        <div className="mt-3 sm:mt-5">
          {isLoading ? (
            <ResourceLoading />
          ) : readOnly && !customer?.id ? (
            <ResourceError
              errors={["Missing selected customer. Cannot load information"]}
            />
          ) : (
            displaySystem()
          )}
        </div>
      </PMDialog>

      <ProductSlideover
        open={productSlideroverOpen}
        onClose={() => setProductSlideoverOpen(false)}
        view="spareparts"
        productId={system?.product?.id}
        module={module}
        moduleParam={moduleParam}
      />
      <ReportHistorySlideover
        open={historySlideoverOpen}
        onClose={() => setHistorySlideoverOpen(false)}
        systemId={system?.id}
      />


    </>
  );
}

CustomerSystemDialog.defaultProps = {
  selected: null,
  // Read only is used when only wanting to display info. Update or creating will not work.
  readOnly: false,
  open: false,
  onClose: () => toast.error("Missing onClose function. Cannot close dialog"),
};
